import React from "react";
import {
  ArrowUpRight,
  BadgeCheck,
  Headphones,
  Sparkles,
  Star,
} from "lucide-react";
import migratedPageSources from "../../data/migratedPageSources.json";
import "./MigratedPage.css";

const pageSources = migratedPageSources;

const photos = {
  mahalia: "/practitioner-thumbnails/d186613c50da267e0407.webp",
  audrina: "/practitioner-thumbnails/d089eb0b4f86b4629ee3.webp",
  karnada: "/practitioner-thumbnails/0a336cd6372c9b13ebc8.webp",
  micki: "/practitioner-thumbnails/8728e56839d687840641.webp",
  aquiera: "/practitioner-thumbnails/a49e5b8550f5d096b56e.webp",
  ebony: "/practitioner-thumbnails/335ad801d3d389e658c3.webp",
  dannielle: "/practitioner-thumbnails/c8103b6d2c5708c5b6d9.webp",
  gordon: "/practitioner-thumbnails/837a401881b8cd93a5a8.webp",
  goddessiman: "/practitioner-thumbnails/4b3d59e4e5ad801ede33.webp",
  "audrina-ongoing-support": "/practitioner-thumbnails/d089eb0b4f86b4629ee3.webp",
  "goddessiman-ongoing-support": "/practitioner-thumbnails/4b3d59e4e5ad801ede33.webp",
};

const photoAlt = {
  mahalia: "Mahalia Thomas, Spiritual Life Coach",
  audrina: "Audrina Divine, Spiritual Life Coach",
  karnada: "Karnada Lewis, The Matriarch Mystic",
  micki: "Micki Mathis, The Trauma Sniper",
  aquiera: "Aquiera Halsey, MPH, Worthiness Coach",
  ebony: "Ebony Goodrich, Past Life Pilot",
  dannielle: "Dannielle Holmes, The Heal Grow Glow Coach",
  gordon: "Gordon Allen, Spiritual Bio-Hacking Coach",
  goddessiman: "GoddessIman, Liberation and Ascension Guide",
  "goddessiman-ongoing-support": "GoddessIman, Liberation and Ascension Guide",
};

const photoBadges = {
  aquiera: "Worthiness Coach · Psychic · Hypnotist",
  ebony: "Past Life Pilot · Hypnotist",
  dannielle: "The Heal Grow Glow Coach",
  gordon: "Spiritual Bio-Hacking Coach",
  goddessiman: "Liberation and Ascension Guide",
};

const pageKinds = {
  mahalia: "practitioner mp-profile-mahalia",
  audrina: "practitioner mp-profile-audrina",
  karnada: "practitioner mp-profile-karnada",
  micki: "practitioner mp-profile-micki",
  "frequency-boost": "frequency",
  "sovereignty-amplified": "intensive",
  "after-the-reset": "followup",
  "detach-protocol": "detach",
  "audrina-ongoing-support": "ongoing mp-packages",
  aquiera: "practitioner mp-profile-aquiera",
  ebony: "practitioner mp-profile-ebony",
  dannielle: "practitioner mp-profile-dannielle",
  gordon: "practitioner mp-profile-gordon",
  goddessiman: "practitioner mp-profile-goddessiman",
  reclaim: "experience mp-soul-series mp-reclaim",
  clarify: "experience mp-soul-series mp-clarify",
  rise: "experience mp-soul-series mp-rise",
  "reset-room": "experience mp-reset-event",
  "goddessiman-ongoing-support": "ongoing mp-packages mp-goddessiman-ongoing",
  podcast: "podcast",
};

const migratedInternalPaths = {
  "our-practitioners/audrina-divine": "/audrina",
  "our-practitioners/aquiera-halsey-mph": "/aquiera",
  "our-practitioners/ebony-goodrich": "/ebony",
  "our-practitioners/dannielle-holmes": "/dannielle",
  "our-practitioners/gordon-allen": "/gordon",
  "our-practitioners/goddessiman": "/goddessiman",
  aquiera: "/aquiera",
  "aquiera-ongoing-support": "/aquiera-ongoing-support",
  ebony: "/ebony",
  dannielle: "/dannielle",
  reclaim: "/reclaim",
  clarify: "/clarify",
  rise: "/rise",
  "reset-room": "/reset-room",
  "goddessiman-ongoing-support": "/goddessiman-ongoing-support",
  gordon: "/gordon",
  goddessiman: "/goddessiman",
};

const stripDecorativeMarks = (value) =>
  value
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, "")
    .replace(/^[\s✦◈✓⚠·•]+\s*/u, "")
    .replace(/[✦◈✓⚠]/gu, "")
    .replace(/\\\|/g, "|")
    .trim();

function parseSource(source) {
  const lines = source
    .replace(/^\s*top of page\s*/i, "")
    .replace(/\s*bottom of page\s*$/i, "")
    .split(/\r?\n/);
  const firstContentIndex = lines.findIndex((line) => line.trim());
  if (
    firstContentIndex >= 0 &&
    !/^(?:!\[|#{1,6}\s|[-*>]\s?)/.test(lines[firstContentIndex].trim())
  ) {
    lines.splice(firstContentIndex, 1);
  }
  const normalized = lines
    .join("\n")
    .replace(/\[!\[([^\]]*)\]\([^)]+\)\]\((https?:\/\/[^)]+)\)/g, "[$1]($2)")
    .replace(
      /\[([^\]]+)\]\(https:\/\/www-thewisdomnetwork-net\.filesusr\.com\/html\/[^)]+#about\)/g,
      "[$1](#about)"
    )
    .replace(
      /\[([^\]]+)\]\(https:\/\/www\.thewisdomnetwork\.net\/our-practitioners\/audrina-divine\)/g,
      "[$1](/audrina)"
    )
    .replace(
      /\[([^\]]+)\]\(https:\/\/www\.thewisdomnetwork\.net\/([^)]*)\)/g,
      (match, label, sourcePath) => {
        const path = sourcePath.replace(/\/$/, "").split(/[?#]/)[0];
        const localPath = migratedInternalPaths[path];
        return localPath ? `[${label}](${localPath})` : match;
      }
    )
    .replace(/!\[[^\]]*\]\(https?:\/\/[^\s]*\)/g, "")
    .replace(/\[[^\]]+\]\(https:\/\/(?:static\.wixstatic\.com|images\.unsplash\.com)\/[^)]*\)/g, "")
    .replace(/The Next Session Starts In\s*\d+\s*Minutes\s*:\s*\d+\s*Seconds/gi, "")
    .replace(/(\d)(?=(?:Expert Healers|Master Healers|Minutes|Hours))/g, "$1 ")
    .replace(/DailySessions/g, "Daily Sessions")
    .replace(/Date & Time(?=June)/g, "Date & Time · ")
    .replace(/Format(?=Virtual)/g, "Format · ")
    .replace(/Duration(?=\d)/g, "Duration · ")
    .replace(/Investment(?=From \$)/g, "Investment · ")
    .replace(/\\\|/g, "|");
  const blocks = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ").trim() });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: "list", items: list });
      list = [];
    }
  };

  normalized.split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
      return;
    }
    if (/^!\[[^\]]*\]\([^)]+\)$/.test(line)) return;
    if (/^\* \* \*$/.test(line)) {
      flushParagraph();
      flushList();
      blocks.push({ type: "rule" });
      return;
    }
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        level: heading[1].length,
        text: heading[2],
      });
      return;
    }
    if (/^-\s+/.test(line)) {
      flushParagraph();
      list.push(line.replace(/^-\s+/, ""));
      return;
    }
    flushList();
    if (/^>\s?/.test(line)) {
      flushParagraph();
      blocks.push({ type: "quote", text: line.replace(/^>\s?/, "") });
      return;
    }
    paragraph.push(line);
  });
  flushParagraph();
  flushList();
  return blocks;
}

function inlineNodes(input) {
  const text = stripDecorativeMarks(input);
  const expression =
    /(\[([^\]]+)\]\(((?:https?:\/\/[^)]+)|(?:#[\w-]+)|(?:\/(?!\/)[^)]+))\)|\*\*(.+?)\*\*|_([^_]+)_|\*(.+?)\*)/g;
  const result = [];
  let last = 0;
  let match;
  let key = 0;

  while ((match = expression.exec(text)) !== null) {
    if (match.index > last) result.push(text.slice(last, match.index));
    if (match[1].startsWith("[")) {
      const external = /^https?:\/\//i.test(match[3]);
      result.push(
        <a
          className="mp-text-link"
          href={match[3]}
          key={`link-${key++}`}
          rel={external ? "noopener noreferrer" : undefined}
          target={external ? "_blank" : undefined}
        >
          {match[2]} <ArrowUpRight aria-hidden="true" size={13} />
        </a>
      );
    } else if (match[4]) {
      result.push(<strong key={`strong-${key++}`}>{match[4] || match[5] || match[6]}</strong>);
    } else {
      result.push(<em key={`em-${key++}`}>{match[5] || match[6]}</em>);
    }
    last = expression.lastIndex;
  }
  if (last < text.length) result.push(text.slice(last));
  return result;
}

function extractLinks(text) {
  const expression =
    /\[([^\]]+)\]\(((?:https?:\/\/[^)]+)|(?:#[\w-]+)|(?:\/(?!\/)[^)]+))\)/g;
  return Array.from(text.matchAll(expression), (match) => ({
    label: stripDecorativeMarks(match[1]),
    href: match[2],
  }));
}

function renderInline(text) {
  return inlineNodes(text);
}

function SourceBlock({ block, className = "" }) {
  if (block.type === "heading") {
    const semanticLevel = block.level === 4 ? 3 : block.level > 4 ? 4 : block.level;
    const Heading = `h${Math.min(semanticLevel, 6)}`;
    return <Heading className={className}>{renderInline(block.text)}</Heading>;
  }
  if (block.type === "list") {
    return (
      <ul className={className}>
        {block.items.map((item, index) => (
          <li key={`${index}-${item.slice(0, 18)}`}>{renderInline(item)}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "rule") return <div className="mp-rule" aria-hidden="true" />;
  if (block.type === "quote") return <blockquote className={className}>{renderInline(block.text)}</blockquote>;

  if (/^\s*★{3,}\s*$/.test(block.text)) {
    return (
      <div className={`mp-stars ${className}`} role="img" aria-label="Five out of five stars">
        {Array.from({ length: 5 }, (_, index) => (
          <Star aria-hidden="true" key={index} size={15} />
        ))}
      </div>
    );
  }
  const clean = stripDecorativeMarks(block.text);
  if (!clean) return null;
  return <p className={className}>{renderInline(clean)}</p>;
}

function isDecorativeParagraph(text) {
  return /^[\s✦◈✓⚠·•!]*$/.test(text);
}

function heroHeadingIndex(blocks) {
  const index = blocks.findIndex((block) => block.type === "heading" && block.level === 1);
  return index;
}

function isCredential(text) {
  return /^✦\s*/.test(text);
}

function isLinkOnlyParagraph(text) {
  const links = extractLinks(text);
  return links.length > 0 && text.replace(/\[[^\]]+\]\(https?:\/\/[^)]+\)/g, "").trim() === "";
}

function getSections(blocks) {
  const sections = [];
  let section = null;
  blocks.forEach((block) => {
    if (block.type === "heading" && block.level === 2) {
      section = { label: "", title: block.text, blocks: [] };
      sections.push(section);
    } else if (section) {
      if (block.type === "p" && sections.length && !section.blocks.length && block.text.length < 64 && !extractLinks(block.text).length) {
        section.label = block.text;
      } else {
        section.blocks.push(block);
      }
    } else {
      if (sections.length && !sections[0].title) {
        sections[0].blocks.push(block);
      } else {
        sections.push({ label: "", title: "", blocks: [block] });
      }
    }
  });
  const preamble = sections[0];
  if (
    preamble &&
    !preamble.title &&
    preamble.blocks[0]?.type === "p" &&
    preamble.blocks[0].text.length < 64 &&
    !extractLinks(preamble.blocks[0].text).length
  ) {
    preamble.label = preamble.blocks.shift().text;
  }
  return sections;
}

function SectionContent({ blocks, testimonial = false }) {
  const elements = [];
  let features = [];
  let feature = [];
  let featureIndex = 0;
  const flushFeature = () => {
    if (feature.length) {
      features.push(
        <article className="mp-feature" key={`feature-${featureIndex++}`}>
          {feature.map((block, index) => (
            <SourceBlock block={block} key={`${index}-${block.text || block.type}`} />
          ))}
        </article>
      );
      feature = [];
    }
  };
  const flushFeatures = () => {
    flushFeature();
    if (features.length) {
      elements.push(
        <div className="mp-group" key={`group-${elements.length}`}>
          {features}
        </div>
      );
      features = [];
    }
  };

  blocks.forEach((block, index) => {
    if (block.type === "heading" && block.level === 3) {
      flushFeature();
      feature = [block];
      return;
    }
    if (feature.length) {
      feature.push(block);
      return;
    }
    if (block.type === "p" && /^\s*★{3,}\s*$/.test(block.text)) {
      flushFeatures();
      elements.push(<SourceBlock block={block} key={`stars-${index}`} />);
      return;
    }
    if (block.type === "p" && isLinkOnlyParagraph(block.text)) {
      flushFeatures();
      const links = extractLinks(block.text);
      elements.push(
        <div className="mp-cta-strip" key={`cta-${index}`}>
          {links.map((link) => (
            <a
              className="mp-button"
              href={link.href}
              key={link.href + link.label}
              rel={/^https?:\/\//i.test(link.href) ? "noopener noreferrer" : undefined}
              target={/^https?:\/\//i.test(link.href) ? "_blank" : undefined}
            >
              {link.label} <ArrowUpRight aria-hidden="true" size={15} />
            </a>
          ))}
        </div>
      );
      return;
    }
    flushFeatures();
    const attribution =
      block.type === "p" && (/^—\s/.test(block.text) || /,\s*Verified Client$/.test(block.text));
    elements.push(
      <SourceBlock
        block={block}
        className={testimonial ? (attribution ? "mp-attribution" : "mp-testimonial") : ""}
        key={`block-${index}`}
      />
    );
  });
  flushFeatures();
  return elements;
}

function PageHero({ slug, blocks }) {
  const ongoingSlug =
    slug === "audrina-ongoing-support" || slug === "goddessiman-ongoing-support";
  const headingIndex = heroHeadingIndex(blocks);
  let heroBlocks = [];
  let remainder = [];
  let platformBlocks = [];

  if (slug === "podcast") {
    const platformStart = blocks.findIndex(
      (block) => block.type === "heading" && /Streaming On/i.test(block.text)
    );
    heroBlocks = platformStart < 0 ? blocks : blocks.slice(0, platformStart);
    platformBlocks = platformStart < 0 ? [] : blocks.slice(platformStart);
  } else if (ongoingSlug) {
    const nameIndex = blocks.findIndex(
      (block) => block.type === "heading" && block.level === 2
    );
    heroBlocks = nameIndex < 0 ? blocks : blocks.slice(0, nameIndex + 1);
    remainder = nameIndex < 0 ? [] : blocks.slice(nameIndex + 1);
  } else if (headingIndex >= 0) {
    const firstSection = blocks.findIndex(
      (block, index) => index > headingIndex && block.type === "heading" && block.level === 2
    );
    const resetIntro = slug === "reset-room"
      ? blocks.findIndex(
        (block, index) =>
          index > headingIndex && block.type === "p" && /^Sound familiar\??$/i.test(block.text)
      )
      : -1;
    const bodyStart = resetIntro > headingIndex ? resetIntro : firstSection;
    heroBlocks = bodyStart < 0 ? blocks : blocks.slice(0, bodyStart);
    remainder = bodyStart < 0 ? [] : blocks.slice(bodyStart);
  } else {
    const firstSection = blocks.findIndex((block) => block.type === "heading" && block.level === 2);
    heroBlocks = firstSection < 0 ? blocks : blocks.slice(0, firstSection);
    remainder = firstSection < 0 ? [] : blocks.slice(firstSection);
  }

  const h1Index = heroBlocks.findIndex((block) => block.type === "heading" && block.level === 1);
  const ongoingTitleIndex = ongoingSlug
    ? heroBlocks.findIndex((block) => block.type === "heading" && block.level === 2)
    : -1;
  let titleBlock = h1Index >= 0
    ? heroBlocks[h1Index]
    : ongoingTitleIndex >= 0
      ? heroBlocks[ongoingTitleIndex]
      : null;
  if (slug === "sovereignty-amplified" && !titleBlock) {
    titleBlock = { type: "heading", level: 1, text: "Sovereignty Amplified" };
  }
  let podcastLead = null;
  if (slug === "podcast" && !titleBlock) {
    const leadIndex = heroBlocks.findIndex(
      (block) => block.type === "p" && /IT'S TIME TO REWIRE YOUR REALITY/i.test(block.text)
    );
    if (leadIndex >= 0) {
      podcastLead = heroBlocks[leadIndex];
      heroBlocks = heroBlocks.filter((_, index) => index !== leadIndex);
    }
  }
  const credentials = heroBlocks.filter((block) => block.type === "p" && isCredential(block.text));
  const sourceLabelIndex = heroBlocks.findIndex(
    (block) =>
      block.type === "p" &&
      !isCredential(block.text) &&
      block.text.length < 90 &&
      !/^Presented by/i.test(block.text) &&
      /The Wisdom Network|\|/.test(block.text)
  );
  const sourceLabelIndexes = new Set(sourceLabelIndex < 0 ? [] : [sourceLabelIndex]);
  heroBlocks.forEach((block, index) => {
    if (block.type !== "p") return;
    const seriesLabel =
      ["reclaim", "clarify", "rise"].includes(slug) &&
      /^(Soul Activation Series|Sessions Running Daily)$/i.test(block.text);
    const eventLabel =
      slug === "reset-room" && /^A 2-Hour Virtual Healing Intensive$/i.test(block.text);
    if (seriesLabel || eventLabel) sourceLabelIndexes.add(index);
  });
  const sourceLabels = heroBlocks.filter((_, index) => sourceLabelIndexes.has(index));
  const heroLinkBlocks = heroBlocks.filter((block) => block.type === "p" && extractLinks(block.text).length);
  const heroLinks = heroLinkBlocks.flatMap((block) => extractLinks(block.text));
  const heroText = heroBlocks.filter(
    (block, index) =>
      block !== titleBlock &&
      !credentials.includes(block) &&
      !sourceLabels.includes(block) &&
      !heroLinkBlocks.includes(block) &&
      !(block.type === "p" && isDecorativeParagraph(block.text)) &&
      !(slug === "podcast" && index === h1Index)
  );
  const h1Removed = titleBlock ? heroText.filter((block) => block !== titleBlock) : heroText;
  const ongoing = ongoingSlug;
  const isPodcast = slug === "podcast";
  const firstHeroLabel = sourceLabels[0];
  const otherHeroLabels = sourceLabels.slice(1);
  const heroImage = photos[slug];

  return (
    <>
      <section className="mp-hero">
        {isPodcast ? (
          <div className="mp-podcast-visual" aria-hidden="true">
            <Headphones />
          </div>
        ) : null}
        <div className="mp-hero-copy">
          {firstHeroLabel && (
            <p className="mp-source-label">{renderInline(firstHeroLabel.text)}</p>
          )}
          {otherHeroLabels.map((block, index) => (
            <p className="mp-overline" key={`source-label-${index}`}>{renderInline(block.text)}</p>
          ))}
          {(titleBlock || podcastLead) && (
            <h1 className="mp-serif">
              {renderInline((titleBlock || podcastLead).text)}
            </h1>
          )}
          {h1Removed.map((block, index) => {
            if (ongoing && block.type === "heading" && block.level >= 4) {
              return <p className="mp-overline" key={`hero-copy-${index}`}>{renderInline(block.text)}</p>;
            }
            if (
              ["reclaim", "clarify", "rise"].includes(slug) &&
              block.type === "p" &&
              /^(?:\d+\s+(?:Expert|Master) Healers|\d+\s+Minutes|Daily Sessions Available)$/i.test(block.text)
            ) {
              return <p className="mp-hero-stat" key={`hero-copy-${index}`}>{renderInline(block.text)}</p>;
            }
            if (block.type === "p" && /ONLY \d+ PRIVATE SESSIONS|^\d+ Days/.test(block.text)) {
              return <p className="mp-overline" key={`hero-copy-${index}`}>{renderInline(block.text)}</p>;
            }
            return <SourceBlock block={block} key={`hero-copy-${index}`} />;
          })}
          {credentials.length > 0 && (
            <ul className="mp-credential-list" aria-label="Practitioner credentials">
              {credentials.map((block, index) => (
                <li key={`credential-${index}`}>{renderInline(block.text.replace(/^✦\s*/, ""))}</li>
              ))}
            </ul>
          )}
          {heroLinks.length > 0 && (
            <div className="mp-actions">
              {heroLinks.map((link, index) => (
                <a
                  className={`mp-button ${index > 0 ? "mp-button-secondary" : ""}`}
                  href={link.href}
                  key={`${link.href}-${link.label}`}
                  rel={/^https?:\/\//i.test(link.href) ? "noopener noreferrer" : undefined}
                  target={/^https?:\/\//i.test(link.href) ? "_blank" : undefined}
                >
                  {link.label} <ArrowUpRight aria-hidden="true" size={15} />
                </a>
              ))}
            </div>
          )}
        </div>
        {heroImage && (
          <div className="mp-profile-photo-frame">
            <img
              alt={photoAlt[slug] || "Practitioner portrait"}
              className="mp-profile-photo"
              decoding="async"
              fetchPriority="high"
              src={heroImage}
            />
            <div className="mp-profile-seal" aria-label="Verified Wisdom Network practitioner">
              <BadgeCheck aria-hidden="true" size={22} />
              <span>{ongoing ? "Ongoing support" : photoBadges[slug] || "Verified TWN practitioner"}</span>
            </div>
          </div>
        )}
      </section>
      <SourceSections blocks={remainder} />
      {isPodcast && <PodcastPlatforms blocks={platformBlocks} />}
    </>
  );
}

function SourceSections({ blocks }) {
  const sections = getSections(blocks);
  if (!sections.length) return null;
  return (
    <div className="mp-content">
      {sections.map((section, index) => (
        <section
          className={`mp-section ${/Client Experiences|More From|What Women Felt|What They're/i.test(section.title) ? "mp-testimonial-section" : ""}`}
          id={
            /^meet\b/i.test(section.label) ||
            /^meet\b/i.test(section.title) ||
            /\b(mahalia\s+thomas|audrina\s+divine|karnada\s+lewis|micki\s+mathis|aquiera\s+halsey|ebony\s+goodrich|dannielle\s+holmes|gordon\s+allen|goddess\s+iman)\b/i.test(
              section.title.replace(/[*_`]/g, "")
            )
              ? "about"
              : undefined
          }
          key={`${section.title}-${index}`}
        >
          {section.label && <p className="mp-overline">{renderInline(section.label)}</p>}
          {section.title && <h2 className="mp-serif">{renderInline(section.title)}</h2>}
          <SectionContent blocks={section.blocks} testimonial={/Client Experiences|More From|What Women Felt|What They're/i.test(section.title)} />
        </section>
      ))}
    </div>
  );
}

function PodcastPlatforms({ blocks }) {
  const label = blocks.find((block) => block.type === "heading");
  const links = blocks.flatMap((block) => (block.type === "p" ? extractLinks(block.text) : []));
  const allNames = ["Spotify", "Apple Podcasts", "iHeartRadio", "Player FM", "Amazon Music", "Audible"];
  const displayName = (label) => {
    if (/spotify/i.test(label)) return "Spotify";
    if (/i.?heart/i.test(label)) return "iHeartRadio";
    if (/player\s?fm/i.test(label)) return "Player FM";
    if (/amazon/i.test(label)) return "Amazon Music";
    if (/audible/i.test(label)) return "Audible";
    if (/apple/i.test(label)) return "Apple Podcasts";
    return label;
  };
  const names = links.map((link) => displayName(link.label));
  const entries = [
    ...links.map((link) => ({ ...link, name: displayName(link.label) })),
    ...allNames.filter((name) => !names.some((existing) => existing.toLowerCase().includes(name.toLowerCase())) && name === "Apple Podcasts").map((name) => ({ name })),
  ];
  return (
    <section className="mp-content mp-platform-section" aria-labelledby="mp-platform-title">
      <div className="mp-section">
        <p className="mp-overline">Listen wherever you tune in</p>
        <h2 id="mp-platform-title" className="mp-serif">{label ? renderInline(label.text) : "Streaming On"}</h2>
        <div className="mp-platforms">
          {entries.map((entry, index) => {
            const Icon = index === 0 ? Headphones : Sparkles;
            const content = <><Icon aria-hidden="true" size={20} /><span>{entry.name}</span>{entry.href && <ArrowUpRight aria-hidden="true" size={14} />}</>;
            return entry.href ? (
              <a className="mp-platform" href={entry.href} key={`${entry.name}-${entry.href}`} rel="noopener noreferrer" target="_blank">{content}</a>
            ) : (
              <div className="mp-platform mp-platform-static" key={entry.name}>{content}</div>
            );
          })}
        </div>
        <p className="mp-final-note">Presented by The Wisdom Network.</p>
      </div>
    </section>
  );
}

function MigratedPage({ slug }) {
  const source = pageSources[slug];
  if (!source || !pageKinds[slug]) {
    return (
      <div className="migrated-page" role="status">
        <div className="mp-content">
          <section className="mp-section">
            <p className="mp-overline">The Wisdom Network</p>
            <h1 className="mp-serif">This page is unavailable.</h1>
          </section>
        </div>
      </div>
    );
  }
  const blocks = parseSource(source);
  return (
    <div className={`migrated-page mp-${pageKinds[slug]}`} data-migrated-slug={slug}>
      <PageHero blocks={blocks} slug={slug} />
    </div>
  );
}

export default MigratedPage;
